import axios from "axios";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { LOCAL_STORAGE_KEYS, setItem, getItem, clearAll } from "./utils/localStorage";
import { markSessionExpired } from "./utils/sessionExpiry";
import { toast } from "react-toastify";
import { isGodMode, isLeavingGodMode, abandonGodMode, godModeBackUrl } from "./godMode/session";
import { markGodModeExpired } from "./godMode/expiry";
import { BLOCKED_CODES, blockedStatusOf } from "./utils/accountStatus";

const AuthContext = createContext({});
const API = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_INVALID = 'AUTH_INVALID';
const GOD_MODE_FORBIDDEN = 'GOD_MODE_FORBIDDEN';
// Texte affiché : le code machine ci-dessus est COMPARÉ, jamais ce libellé.
const GOD_MODE_FORBIDDEN_MESSAGE = "Cette action n'est pas disponible en mode intervention.";
const BLOCKED_PAGE = '/compte-suspendu';
let redirectingToSignin = false;
let redirectingToBackOffice = false;
let redirectingToBlockedPage = false;
const publicEmployeePage = () => {
    const path = new URL(window.location.href, 'http://localhost').pathname;
    return ['/survey-end', '/mentions-legales', '/confidentialite'].includes(path.replace(/\/+$/, '')) || /^\/surveys\/(?:[^/]+\/){3,4}[^/]+\/?$/.test(path);
};

const validIdentity = user => user && user.id && ['admin', 'manager'].includes(user.accessRole);
export const sessionLanding = user => blockedStatusOf(user) ? BLOCKED_PAGE : user.accessRole === 'manager' ? '/manager/results' : user.companyId ? '/dashboard' : '/onboarding';

function persistIdentity(token, user) {
    if (!token || !validIdentity(user)) throw new Error('Identité du compte invalide.');
    // Replace all identity/branding together so another account cannot retain
    // the prior account's image, company or role across login and invitation.
    clearAll();
    setItem(LOCAL_STORAGE_KEYS.TOKEN, token);
    setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(user));
    setItem(LOCAL_STORAGE_KEYS.USER_ID, user.id);
    setItem(LOCAL_STORAGE_KEYS.COMPANY_ID, user.companyId);
    setItem(LOCAL_STORAGE_KEYS.COMPANY_NAME, user.companyName);
    setItem(LOCAL_STORAGE_KEYS.COMPANY_LOGO, user.companyImage);
    setItem(LOCAL_STORAGE_KEYS.USER_FIRST_NAME, user.firstName);
    setItem(LOCAL_STORAGE_KEYS.USER_LAST_NAME, user.lastName);
    setItem(LOCAL_STORAGE_KEYS.USER_IMAGE, user.image);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
}

export function installSession({ token, user }) {
    persistIdentity(token, user);
    window.location.href = sessionLanding(user);
}

const initialToken = getItem(LOCAL_STORAGE_KEYS.TOKEN);
if (initialToken) axios.defaults.headers.common.Authorization = `Bearer ${initialToken}`;

axios.interceptors.response.use(
    response => response,
    error => {
        const code = error.response?.data?.code;
        // L'appel de fin d'intervention gère lui-même son échec.
        if (error.config?.skipSessionHandling) return Promise.reject(error);
        // Retour au back-office en cours : les requêtes encore en vol échouent avec
        // le jeton de l'intervention qui vient d'être abandonné. Les traiter ici
        // comme une session personnelle expirée EFFACERAIT la session client
        // personnelle du super admin (le localStorage redevient lisible).
        if (redirectingToBackOffice || isLeavingGodMode()) return Promise.reject(error);

        // God Mode (SPEC §4.3, A10) : la session personnelle du navigateur n'est
        // jamais en jeu, et `/signin` n'est jamais la destination.
        if (isGodMode()) {
            if (code === AUTH_INVALID) {
                // Session d'intervention morte (expirée, close, administrateur
                // plateforme désactivé) : retour à la fiche du compte, avec
                // le message qui dit pourquoi.
                if (!redirectingToBackOffice) {
                    redirectingToBackOffice = true;
                    const retour = godModeBackUrl();
                    abandonGodMode();
                    markGodModeExpired();
                    window.location.href = retour;
                }
            } else if (error.response?.status === 403 && code === GOD_MODE_FORBIDDEN) {
                // La session continue : seul cet écran n'a pas le droit.
                toast.error(GOD_MODE_FORBIDDEN_MESSAGE, { toastId: 'god-mode-forbidden' });
            }
            return Promise.reject(error);
        }

        const hasSession = !!getItem(LOCAL_STORAGE_KEYS.TOKEN);
        // Only the authentication code expires a session. A scope denial asks
        // for fresh database authorization while retaining a valid token.
        if (hasSession && code === AUTH_INVALID && !redirectingToSignin) {
            redirectingToSignin = true;
            clearAll();
            delete axios.defaults.headers.common.Authorization;
            markSessionExpired();
            if (!publicEmployeePage()) window.location.href = '/signin';
        } else if (hasSession && error.response?.status === 403 && BLOCKED_CODES.includes(code)) {
            // Entreprise suspendue ou clôturée : la session reste valide (l'écran
            // dédié en a besoin pour se déconnecter proprement), on n'efface rien.
            const surLEcran = new URL(window.location.href, 'http://localhost').pathname === BLOCKED_PAGE;
            if (!redirectingToBlockedPage && !surLEcran && !publicEmployeePage()) {
                redirectingToBlockedPage = true;
                window.location.href = BLOCKED_PAGE;
            }
        } else if (hasSession && error.response?.status === 403 && code === 'MANAGER_SCOPE_FORBIDDEN') {
            window.dispatchEvent(new Event('bsrate:access-changed'));
        }
        return Promise.reject(error);
    }
);

export const AuthProvider = ({ children }) => {
    // Stored identity is never parsed or authorized. Only the token initiates
    // bootstrap; children receive no identity until /auth/me succeeds.
    const [session, setSession] = useState(() => ({ authData: null, status: getItem(LOCAL_STORAGE_KEYS.TOKEN) ? 'loading' : 'anonymous', error: null }));
    const generation = useRef(0);
    const mounted = useRef(false);
    const refreshing = useRef(false);

    const refreshSession = useCallback(async () => {
        if (refreshing.current) return;
        const requestId = ++generation.current;
        const token = getItem(LOCAL_STORAGE_KEYS.TOKEN);
        if (!token) {
            delete axios.defaults.headers.common.Authorization;
            setSession({ authData: null, status: 'anonymous', error: null });
            return;
        }
        refreshing.current = true;
        setSession({ authData: null, status: 'loading', error: null });
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        try {
            const response = await axios.get(`${API}/api/v2/auth/me`);
            if (!mounted.current || requestId !== generation.current || getItem(LOCAL_STORAGE_KEYS.TOKEN) !== token) return;
            const user = response.data?.user;
            if (!validIdentity(user)) throw new Error('Les droits du compte n’ont pas pu être vérifiés.');
            persistIdentity(token, user);
            setSession({ authData: { token, user }, status: 'ready', error: null });
        } catch (error) {
            // The interceptor owns the private-page hard redirect. Rendering
            // an anonymous SPA route here would mount signin in the old
            // document and consume its expiry relay before the new page loads.
            const hardNavigationPending = redirectingToSignin && !publicEmployeePage();
            if (mounted.current && requestId === generation.current && !hardNavigationPending) {
                setSession({ authData: null, status: getItem(LOCAL_STORAGE_KEYS.TOKEN) ? 'error' : 'anonymous', error });
            }
        } finally {
            if (requestId === generation.current) refreshing.current = false;
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        refreshSession();
        const refresh = () => refreshSession();
        window.addEventListener('bsrate:access-changed', refresh);
        return () => {
            mounted.current = false;
            generation.current += 1;
            refreshing.current = false;
            window.removeEventListener('bsrate:access-changed', refresh);
        };
    }, [refreshSession]);

    const signIn = async (email, password) => {
        const response = await axios.post(`${API}/api/v2/auth/login`, { email, password });
        installSession(response.data);
    };
    const signOut = () => {
        generation.current += 1;
        refreshing.current = false;
        clearAll();
        delete axios.defaults.headers.common.Authorization;
        setSession({ authData: null, status: 'anonymous', error: null });
    };
    return <AuthContext.Provider value={{ authData: session.authData, sessionStatus: session.status, sessionError: session.error,
        loading: session.status === 'loading', refreshSession, signIn, signOut }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
